import { IConvertedImages } from '../../../infrastructure/library/pictrans/ImageConverter';
import { Result } from '../../../shared/Result';
import { WebSubCategory } from '../category/WebSubCategory';
import { ArticleType } from '../comment/Comment';

export const ContentsType = {
  SUBCATEGORY_RANKING: 0,
  FEATURE: 1,
  TOPICS: 2,
  TOPICS_WIDE: 3
} as const;
export type ContentsType = (typeof ContentsType)[keyof typeof ContentsType];

export const ViewType = {
  ARTICLE: 0,
  ARTICLE_WIDE: 1,
  VIDEO: 2,
  AD_INFEED: 3,
  AD_SPONSORED: 4,
  AD_YJ_APP: 5,
  AD_STB: 6,
  ARTICLE_EMPTY: 7
} as const;
export type ViewType = (typeof ViewType)[keyof typeof ViewType];

export const ThumbnailType = {
  SMALL: 0,
  MIDDLE: 1,
  LARGE: 2
} as const;
export type ThumbnailType = (typeof ThumbnailType)[keyof typeof ThumbnailType];

/**
 * User Action Loggerのcontent_idの種別
 */
export const ContentIdType = {
  // shannonに入稿されている記事の種別
  SHANNON_ARTICLE: 'shannon_article',
  // 特集・エキスパート・自動再生動画などのshannonに入稿されていない記事の種別
  NEWS_OTHER: 'news_other'
} as const;
export type ContentIdType = (typeof ContentIdType)[keyof typeof ContentIdType];

export interface INewsFeedItem {
  readonly title: string;
  readonly link: string;
  readonly isApproachLink: boolean;
  readonly mediaName: string;
  readonly mediaId?: string;
  readonly author?: string;
  readonly thumbUrl?: string;
  readonly resizedThumbUrl: IConvertedImages | null;
  readonly wideThumbUrl?: string;
  readonly resizedWideThumbUrl: IConvertedImages | null;
  readonly platform?: string;
  readonly contentsType?: ContentsType;
  readonly viewType: typeof ViewType.ARTICLE | typeof ViewType.ARTICLE_WIDE;
  readonly video?: {
    readonly duration: number;
  };
  readonly ultSlk?: string;
  readonly mitInfo?: string;
  readonly contentId?: string;
  readonly contentIdType: string;
  readonly createUnixTimeStamp?: number;
  readonly createTimeISO8601?: string;
  readonly isPay: boolean;
  readonly taxIncludedPrice?: number;
  readonly crop?: {
    readonly cropX?: number;
    readonly cropY?: number;
    readonly cropWidth?: number;
    readonly cropHeight?: number;
    readonly originalWidth?: number;
    readonly originalHeight?: number;
    readonly isAllowedToTrim: boolean;
    readonly isTrimSensitive: boolean;
  };
  readonly isCommentModule?: boolean;
  readonly isAuthorCommentModule?: boolean;
  readonly authorName?: string;
  readonly authorTitle?: string;
  readonly authorThumbUrl?: string;
  readonly commentText?: string;
  readonly photoPageLink?: string;
  readonly hashTag?: ReadonlyArray<string>;
  readonly articleType?: ArticleType;
}

export interface INewsFeedInfeedAdItem {
  readonly viewType: typeof ViewType.AD_INFEED;
}
export interface INewsFeedSponsordAdItem {
  readonly viewType: typeof ViewType.AD_SPONSORED;
}

interface INewsFeedExperiment {
  readonly experimentId: string;
  readonly bucketId: string;
}

export interface INewsFeed {
  // 記事のみの件数
  articleCount: number;
  // リクエスト数に対して記事本数が下回ったときにtrueを返す
  isLimit: boolean;
  items: Array<INewsFeedItem | INewsFeedInfeedAdItem | INewsFeedSponsordAdItem>;
  // timelinePFのABテスト用
  experiments?: Array<INewsFeedExperiment>;
  // パーソナライズされているかどうか
  isPersonalized?: boolean;
}

export type GetNewsFeedError =
  | { type: 'INVALID_PAGE_NUMBER'; message: string }
  | { type: 'NO_ROLE_TOKEN'; message: string }
  | { type: 'TLPF_FAILURE'; message: string }
  | { type: 'CLEAN_RANKING_FAILURE'; message: string; cause?: unknown };

export const isNewsFeedItem = (
  item: INewsFeedItem | INewsFeedInfeedAdItem | INewsFeedSponsordAdItem
): item is INewsFeedItem => {
  return (item as INewsFeedItem).title !== undefined;
};

export default interface INewsFeedRepository {
  /**
   * ニュースフィードを取得する。
   *
   * @param cookieHeader Cookieヘッダ
   * @param page ページ番号。便宜上ページ番号としているが、各ページごとに取得するデータおよびデータ数は異なる。
   * @returns ニュースフィード
   */
  getNewsFeed(cookieHeader: string, page: number): Promise<Result<INewsFeed, GetNewsFeedError>>;
  getFeedBySubcategoryIds(
    subCategories: readonly WebSubCategory[],
    start: number,
    results: number
  ): Promise<Result<INewsFeed>>;
  getPhotoFeedBySubcategoryIds(
    subCategories: WebSubCategory[],
    start: number,
    results: number
  ): Promise<Result<INewsFeed>>;
}
